import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { IReservation } from '../../shared/booking/model/reservation.model';
import { SERVER_API_URL } from '../../app.constants';
import { createRequestOption } from '../../shared/util/request-util';
import dayjs from 'dayjs';
import { LocalStorageService } from 'ngx-webstorage';

type EntityResponseType = HttpResponse<IReservation>;
type EntityArrayResponseType = HttpResponse<IReservation[]>;

@Injectable({ providedIn: 'root' })
export class ReservationService {
  public resourceUrl = SERVER_API_URL + 'api/reservations';
  onCancelStatus = new Subject();

  constructor(
    protected http: HttpClient,
    private localStorageService: LocalStorageService
  ) {}

  cancelStatus(): void {
    this.onCancelStatus.next(true);
  }

  create(reservation: IReservation): Observable<EntityResponseType> {
    const copy = this.convertDateFromClient(reservation);
    return this.http
      .post<IReservation>(this.resourceUrl, copy, { observe: 'response' })
      .pipe(map((res: EntityResponseType) => this.convertDateFromServer(res)));
  }

  createWithCustomers(
    reservation: IReservation,
    customerIds: number[],
    utilityIds: number[],
    resourceIds: number[]
  ): Observable<EntityResponseType> {
    const copy = this.convertDateFromClient(reservation);
    return this.http
      .post<IReservation>(
        `${this.resourceUrl}WithCustomers`,
        { reservation: copy, customerIds, utilityIds, resourceIds },
        { observe: 'response' }
      )
      .pipe(map((res: EntityResponseType) => this.convertDateFromServer(res)));
  }

  createWithCustomersAndDays(
    reservation: IReservation,
    customerIds: number[],
    utilityIds: number[],
    resourceIds: number[],
    days: number[],
    startDate: Date,
    endDate: Date
  ): Observable<EntityResponseType> {
    const copy = this.convertDateFromClient(reservation);
    return this.http
      .post<IReservation>(
        `${this.resourceUrl}WithCustomersAndDays`,
        { reservation: copy, customerIds, utilityIds, resourceIds, days, startDate, endDate },
        { observe: 'response' }
      )
      .pipe(map((res: EntityResponseType) => this.convertDateFromServer(res)));
  }

  update(reservation: IReservation): Observable<EntityResponseType> {
    const copy = this.convertDateFromClient(reservation);
    return this.http
      .put<IReservation>(this.resourceUrl, copy, { observe: 'response' })
      .pipe(map((res: EntityResponseType) => this.convertDateFromServer(res)));
  }

  statusUpdate(reservation: IReservation): Observable<HttpResponse<any>> {
    return this.http.put<IReservation>(this.resourceUrl + '/update-status', reservation, {
      observe: 'response',
    });
  }

  statusUpdateForCustomer(
    reservation: IReservation,
    customerId: number
  ): Observable<HttpResponse<any>> {
    return this.http.put<IReservation>(
      this.resourceUrl + '/update-status-for-customer/' + customerId + '/-1',
      reservation,
      {
        observe: 'response',
      }
    );
  }

  statusUpdateForCustomerAll(
    reservation: IReservation,
    customerId: number
  ): Observable<HttpResponse<any>> {
    return this.http.put<IReservation>(
      this.resourceUrl + '/update-status-for-customer-all/' + customerId + '/-1',
      reservation,
      {
        observe: 'response',
      }
    );
  }

  find(id: number): Observable<EntityResponseType> {
    return this.http
      .get<IReservation>(`${this.resourceUrl}/${id}`, { observe: 'response' })
      .pipe(map((res: EntityResponseType) => this.convertDateFromServer(res)));
  }

  query(req?: any): Observable<EntityArrayResponseType> {
    const options = createRequestOption(req);
    return this.http
      .get<IReservation[]>(this.resourceUrl, { params: options, observe: 'response' })
      .pipe(map((res: EntityArrayResponseType) => this.convertDateArrayFromServer(res)));
  }

  getByQR(qrCode: string): Observable<HttpResponse<any>> {
    return this.http.get<any>(this.resourceUrl + '/get-reservation-by-qr/' + qrCode, {
      observe: 'response',
    });
  }

  getById(reservationId: string): Observable<HttpResponse<any>> {
    return this.http.get<any>(this.resourceUrl + '/get-reservation-by-id/' + reservationId, {
      observe: 'response',
    });
  }

  getByIds(reservationIds: number[]): Observable<HttpResponse<any>> {
    return this.http.post<any>(
      this.resourceUrl + '/get-reservations-by-ids',
      reservationIds,
      { observe: 'response' }
    );
  }

  delete(id: number): Observable<HttpResponse<{}>> {
    return this.http.delete(`${this.resourceUrl}/${id}`, { observe: 'response' });
  }

  updateResourceOnReservation(reservationId: number, resourceId: number): Observable<string> {
    const headers = new HttpHeaders().set('Content-Type', 'text/plain; charset=utf-8');
    return this.http
      .put<string>(`${this.resourceUrl}-update-resource/${reservationId}/${resourceId}/`, null, {
        headers,
        responseType: 'text' as 'json',
      })
      .pipe(map((res: any) => res));
  }

  protected convertDateFromClient(reservation: IReservation): IReservation {
    const copy: IReservation = Object.assign({}, reservation, {
      startReservationLocalDate:
        reservation.startReservationLocalDate && reservation.startReservationLocalDate.isValid()
          ? reservation.startReservationLocalDate.toJSON()
          : undefined,
      endReservationLocalDate:
        reservation.endReservationLocalDate && reservation.endReservationLocalDate.isValid()
          ? reservation.endReservationLocalDate.toJSON()
          : undefined,
    });
    return copy;
  }

  protected convertDateFromServer(res: EntityResponseType): EntityResponseType {
    if (res.body) {
      res.body.startReservationLocalDate = res.body.startReservationLocalDate
        ? dayjs(res.body.startReservationLocalDate)
        : undefined;
      res.body.endReservationLocalDate = res.body.endReservationLocalDate
        ? dayjs(res.body.endReservationLocalDate)
        : undefined;
    }
    return res;
  }

  protected convertDateArrayFromServer(res: EntityArrayResponseType): EntityArrayResponseType {
    if (res.body) {
      res.body.forEach((reservation: IReservation) => {
        reservation.startReservationLocalDate = reservation.startReservationLocalDate
          ? dayjs(reservation.startReservationLocalDate)
          : undefined;
        reservation.endReservationLocalDate = reservation.endReservationLocalDate
          ? dayjs(reservation.endReservationLocalDate)
          : undefined;
      });
    }
    return res;
  }

  downloadPDF(
    startDate: number,
    endDate: number,
    resourceId: string,
    selectedStatus: number
  ): Observable<Blob> {
    if (resourceId === undefined || resourceId === null) resourceId = '-1';
    const locale = this.localStorageService.retrieve('locale');
    const lang = locale || 'sr';
    return this.http.post(
      `${SERVER_API_URL}api/report/reservation-overview-per-utility/${resourceId}/${startDate}/${endDate}/${selectedStatus}/` +
        lang,
      null,
      {
        responseType: 'blob',
      }
    );
  }

  getResources(resourceId: number): Observable<EntityArrayResponseType> {
    if (resourceId === null) resourceId = -1;
    return this.http.get<any>(`${this.resourceUrl}/get-reservation-resources/${resourceId}`, {
      observe: 'response',
    });
  }
}
